import { z } from "zod";
import { Timestamp } from "firebase/firestore";

// stripe_api_key / stripe_connect acceptés (ordre Firestore) en plus de stripe | paypal
export const PaymentInfoSchema = z.object({
  status: z.enum(["pending", "paid", "failed", "canceled", "disputed", "refunded_partial", "refunded_full"]),
  paymentIntentId: z.string().nullable().optional(),
  method: z
    .union([
      z.literal("stripe"),
      z.literal("paypal"),
      z.literal("stripe_connect"),
      z.literal("stripe_api_key"),
    ])
    .nullable()
    .optional(),
  paidAt: z.any().nullable().optional(), // Accept Timestamp objects from Firebase
  failedAt: z.any().nullable().optional(), // Accept Timestamp objects from Firebase
  chargeId: z.string().nullable().optional(), // ID de la transaction Stripe
  // IDs Stripe côté vendeur (compte Connect)
  transferId: z.string().nullable().optional(),          // ID du transfer tr_...
  destinationPaymentId: z.string().nullable().optional(), // ID du paiement côté compte connecté (py_..., ch_..., etc.)
  applicationFeeId: z.string().nullable().optional(),    // ID de l'application_fee fee_...
  settlement_status: z.enum(["awaiting_transfer", "transferred", "failed"]).nullable().optional(), // Statut de règlement spécifique au produit
  commission: z.number().nullable().optional(), // Commission en centimes
  netAmount: z.number().nullable().optional(), // Montant net en centimes (après commission)
  commissionRate: z.number().nullable().optional(), // Taux de commission en pourcentage
  fixedCommission: z.number().nullable().optional(), // Commission fixe en centimes (EUR)
  currency: z.string().nullable().optional(), // Devise (par défaut "eur")
  paymentAttempts: z.array(z.any()).nullable().optional(),
});

export const RefundInfoSchema = z.object({
  status: z.enum(["credited", "refunded"]),
  amount: z.number(),
  reason: z.string(),
  creditTransactionId: z.string().nullable().optional(),
  refundedAt: z.string(),
});

export const ProductSchema = z.object({
  reference: z.string(),
  model: z.string(),
  amount: z.string(),
  tiktokUsername: z.string(),
  paymentInfo: PaymentInfoSchema,
  preparationStatus: z.enum(["todo", "in_progress", "ready"]).nullable().optional(),
  refundInfo: RefundInfoSchema.nullable().optional(),
  amountRefunded: z.number().nullable().optional(), // Montant déjà remboursé en centimes (depuis Stripe)
  refundIds: z.array(z.string()).nullable().optional(), // Liste des IDs de remboursements Stripe
  returnStatus: z.enum(["requested", "in_transit", "delivered", "completed", "refunded"]).nullable().optional(), // Statut du retour pour ce produit
  createdAt: z.any().nullable().optional(), // Accept Timestamp objects from Firebase
  updatedAt: z.any().nullable().optional(), // Accept Timestamp objects from Firebase
});

export const PickupPointSchema = z.object({
  id: z.string(),
  /** Code Sendcloud / transporteur (ex. FR93266) pour création d’étiquette Mondial Relay Connect */
  code: z.string().optional(),
  name: z.string(),
  address: z.string(),
  city: z.string(),
  postalCode: z.string(),
  country: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  distance: z.number().nullable().optional(),
  openingHours: z.string().nullable().optional(),
  provider: z.enum(["chronopost", "mondial_relay"]),
});

export const OrderDataSchema = z.object({
  organizationName: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  phone: z.string(),
  tiktokUsername: z.string(),
  productReference: z.string(),
  productModel: z.string().optional(),
  amount: z.string(),
  address: z.string(),
  fullAddress: z.string().optional(),
  houseNumber: z.string().optional(), // Numéro de rue extrait de Google Maps
  city: z.string(),
  postalCode: z.string(),
  country: z.string(),
  deliveryMethod: z.string().nullable(),
  shippingCost: z.number().optional(),
  // Informations dynamiques de la méthode de livraison (provenant de Sendcloud/configuration)
  methodName: z.string().optional(),
  methodDescription: z.string().optional(),
  provider: z.string().optional(),
  methodId: z.number().optional(),
  estimatedHours: z.number().optional(),
  minWeight: z.number().optional(),
  maxWeight: z.number().optional(),
  servicePointInput: z.string().optional(),
  isHomeDelivery: z.boolean().optional(),
  products: z.array(ProductSchema).optional(),
  pickupPoint: PickupPointSchema.optional(),
  productItems: z.array(z.object({
    modelName: z.string(),
    size: z.string()
  })).optional(),
});

export const FirebaseOrderSchema = z.object({
  id: z.string().nullable().optional(),
  customerId: z.string().nullable().optional(), // ID du customer (identifiant business unique)
  organizationId: z.string(),
  organizationName: z.string().nullable().optional(),
  orderInfo: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().email(),
    phone: z.string(),
  }),
  products: z.array(ProductSchema),
  deliveryInfo: z.object({
    // SUPPRIMÉ: method (redondant avec methodId)
    method: z.string().nullable().optional(), // ID de la méthode en string (ex: "colissimo", "28036")
    methodName: z.string().nullable().optional(), // Nom lisible de la méthode de livraison
    methodDescription: z.string().nullable().optional(), // Description de la méthode
    provider: z.string().nullable().optional(), // Fournisseur de livraison (sendcloud, mondial_relay, …)
    deliveryMethod: z.string().nullable().optional(), // ID dans shippingConfig.methods
    // ID Sendcloud / numérique legacy
    // Avec les migrations, on peut recevoir number | string | null | undefined | NaN
    methodId: z
      .union([z.number(), z.string(), z.null(), z.undefined(), z.nan()])
      .transform((val) => {
        if (val === null || val === undefined) return undefined;
        const num = typeof val === "string" ? Number(val) : val;
        return isNaN(num as number) ? undefined : num;
      })
      .optional(),
    estimatedHours: z.number().nullable().optional(), // Délai estimé en heures
    // Poids minimum : peut contenir des valeurs incohérentes après migration (string, null, undefined, NaN)
    minWeight: z
      .union([z.number(), z.string(), z.null(), z.undefined(), z.nan()])
      .transform((val) => {
        if (val === null || val === undefined) return undefined;
        const num = typeof val === "string" ? Number(val) : val;
        return isNaN(num as number) ? undefined : num;
      })
      .optional(),
    // Poids maximum : idem
    maxWeight: z
      .union([z.number(), z.string(), z.null(), z.undefined(), z.nan()])
      .transform((val) => {
        if (val === null || val === undefined) return undefined;
        const num = typeof val === "string" ? Number(val) : val;
        return isNaN(num as number) ? undefined : num;
      })
      .optional(),
    servicePointInput: z.string().nullable().optional(), // Type de point de service requis
    isHomeDelivery: z.union([z.boolean(), z.null()]).transform(val => val ?? undefined).optional(), // Si c'est une livraison à domicile
    customShippingPriceCents: z.number().nullable().optional(),
    shippingCost: z.number().nullable().optional(),
    address: z.string().nullable().optional(),
    addressLine2: z.string().nullable().optional(), // Complément d'adresse (bâtiment, étage, appartement)
    fullAddress: z.string().nullable().optional(),
    houseNumber: z.string().nullable().optional(), // Numéro de rue extrait de Google Maps
    city: z.string().nullable().optional(),
    postalCode: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    pickupPoint: PickupPointSchema.nullable().optional(),
    // Statut de paiement de la livraison : on aligne sur PaymentInfo.status
    // pour garder la même sémantique côté livraison et côté produits.
    paymentStatus: z
      .enum([
        "pending",
        "paid",
        "failed",
        "canceled",
        "disputed",
        "refunded_partial",
        "refunded_full",
        "refunded", // livraison remboursée (tous produits remboursés)
      ])
      .optional(),
    // Informations complètes de la méthode de livraison
    methodPrice: z.number().nullable().optional(), // Prix de la méthode
    methodCurrency: z.string().nullable().optional(), // Devise de la méthode
    methodStatus: z.string().nullable().optional(), // Statut de la méthode (active, inactive, etc.)
    // ID adresse expéditeur Sendcloud (sender_address) - legacy v2
    sendcloudSenderAddressId: z.number().nullable().optional(),
    // Adresse expéditeur complète (from_address) pour Sendcloud v3 - snapshot au moment de la commande
    sendcloudSenderAddress: z.object({
      name: z.string().optional(),
      address_line_1: z.string().optional(),
      house_number: z.string().optional(),
      postal_code: z.string().optional(),
      city: z.string().optional(),
      country_code: z.string().optional(),
      company_name: z.string().optional(),
      address_line_2: z.string().optional(),
      email: z.string().optional(),
      phone_number: z.string().optional(),
      state_province_code: z.string().optional(),
      po_box: z.string().optional(),
    }).nullable().optional(),
    // SUPPRIMÉ: trackingCreatedAt et trackingUpdatedAt (redondants avec createdAt et updatedAt au niveau de la commande)
    // Nouveau : statut d'expédition mis à jour par le webhook Sendcloud
    // Informations de remboursement Sendcloud / frais de livraison
    sendcloudRefund: z
      .object({
        type: z.string().optional().nullable(), // ex: 'label_refund'
        status: z.string().optional().nullable(), // ex: 'completed' | 'partial' | 'sendcloud_only'
        sendcloudRefundId: z.union([z.string(), z.null()]).optional().nullable(),
        sendcloudRefundAmount: z.number().optional().nullable(), // En centimes
        shippingRefundAmount: z.number().optional().nullable(), // En centimes
        eligible: z.boolean().optional().nullable(),
        daysSinceCreation: z.number().nullable().optional(),
        error: z.string().optional().nullable(),
        completedAt: z.any().optional().nullable(), // Timestamp Firestore
      })
      .optional(),
    // PaymentIntent qui inclut les frais de livraison (premier produit)
    deliveryPaymentIntentId: z.string().nullable().optional(),
    // Marqué par le backend quand un colis doit être généré
    needsParcelGeneration: z.boolean().optional(),
    // Erreur lors de la création du colis Sendcloud
    parcelCreationError: z
      .object({
        message: z.string(),
        code: z.union([z.string(), z.number()]).optional(),
        timestamp: z.any(),
        fullError: z.any().optional(),
      })
      .optional()
      .nullable(),
  }),
  parcelGenerated: z
    .object({
      version: z.number(),
      createdAt: z.any(),
      provider: z.string().nullable().optional(),
      carrier: z.string().nullable().optional(),
      deliveryMethod: z.string().nullable().optional(),
      methodId: z.number().nullable().optional(),
      methodName: z.string().nullable().optional(),
      methodDescription: z.string().nullable().optional(),
      isHomeDelivery: z.boolean().nullable().optional(),
      country: z.string().nullable().optional(),
      weightKg: z.number().nullable().optional(),
      customerPaidShippingCents: z.number().nullable().optional(),
      methodPrice: z.number().nullable().optional(),
      methodCurrency: z.string().nullable().optional(),
      methodStatus: z.string().nullable().optional(),
      pickupPoint: z
        .object({
          id: z.string().nullable().optional(),
          code: z.string().nullable().optional(),
          name: z.string().nullable().optional(),
          postalCode: z.string().nullable().optional(),
          country: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
      trackingNumber: z.string().nullable().optional(),
      trackingUrl: z.string().nullable().optional(),
      labelPrinterUrl: z.string().nullable().optional(),
      dateAnnounced: z.string().nullable().optional(),
      labelPrinted: z.boolean().nullable().optional(),
      labelPrintedAt: z.any().nullable().optional(),
      parcelId: z.union([z.number(), z.string(), z.null()]).optional(),
      shipmentId: z.union([z.number(), z.string(), z.null()]).optional(),
      carrierCode: z.string().nullable().optional(),
      shippingMethod: z.union([z.number(), z.string(), z.null()]).optional(),
      servicePoint: z.union([z.number(), z.string(), z.null()]).optional(),
      shippingStatusId: z.union([z.number(), z.string(), z.null()]).optional(),
      shippingStatusMessage: z.string().nullable().optional(),
      shippingStatusUpdatedAt: z.any().nullable().optional(),
      lastKnownParcelPriceCents: z.number().nullable().optional(),
      lastKnownParcelWeightGrams: z.number().nullable().optional(),
      lastParcelPriceCheckAt: z.any().nullable().optional(),
      sendcloudOriginalPriceCents: z.number().nullable().optional(),
      sendcloudPriceWithMarkupCents: z.number().nullable().optional(),
    })
    .optional()
    .nullable(),
  preparationStatus: z.enum(["todo", "in_progress", "ready"]).nullable().optional(),
  status: z.string().nullable().optional(), // Rétrocompatibilité - Statut global de la commande

  // Statut global du retour
  returnStatus: z.enum(["none", "requested", "in_transit", "delivered", "completed", "refunded"]).nullable().optional(),

  // Détails du retour
  return: z.object({
    returnId: z.number().optional(),
    incomingParcelId: z.number().optional(),
    trackingNumber: z.string().optional(),
    labelUrl: z.string().optional(),
    status: z.string().optional(),
    createdAt: z.any().optional(),
  }).optional().nullable(),

  /** Livraison gérée par la plateforme (Sendcloud central) vs vendeur */
  shippingMode: z.enum(["platform", "seller"]).nullable().optional(),

  currentStep: z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal("complete"),
  ]).nullable().optional(),
  // status supprimé - le statut est géré au niveau des produits dans products[].paymentInfo.status
  createdAt: z.any().nullable().optional(), // Timestamp | FieldValue
  updatedAt: z.any().nullable().optional(), // Timestamp | FieldValue
  // Timestamp racine: dernière création de ligne produit
  lastProductCreatedAt: z.any().nullable().optional(),
  // Note: amount_total, amount_refunded, currency, paymentIntentId, payment_status, settlement_status
  // sont gérés UNIQUEMENT au niveau des produits dans products[].paymentInfo
  // Ces champs ne doivent PAS être à la racine de l'order

  // Snapshot de la config de fusion au moment de la création de la commande
  // Permet d'éviter les effets rétroactifs lors de changements de config
  // Stocke les DATES EXACTES de la période de fusion (pas les jours de la semaine)
  // Logique :
  // - Si wasCreatedInOpenDays = true → fusion possible si now est entre periodStart et periodEnd
  // - Si wasCreatedInOpenDays = false → fusion le même jour uniquement (periodStart = periodEnd = jour de création)
  mergeConfigSnapshot: z.object({
    periodStart: z.string().optional(), // Date ISO de début de période
    periodEnd: z.string().optional(),   // Date ISO de fin de période
    wasCreatedInOpenDays: z.boolean().optional(),
  }).nullable().optional(),

  // Historique des dettes/crédits pour cette commande (remplace organization_balance_history)
  debtHistory: z.array(z.object({
    id: z.string(),
    delta: z.number(), // Montant en centimes (positif = dette, négatif = crédit)
    balanceBefore: z.number(),
    balanceAfter: z.number(),
    reason: z.string(),
    createdAt: z.any().optional(), // Timestamp | string
    paymentIntentId: z.string().optional(),
    refundId: z.string().optional(),
    transferId: z.string().optional(),
    productIndex: z.number().optional(),
    note: z.string().optional(),
    actor: z.union([
      z.object({
        type: z.enum(["system", "user", "admin"]),
        id: z.string().optional(),
        email: z.string().optional(),
        name: z.string().optional(),
      }),
      z.string(), // Backwards compatibility: accept string actor
    ]).optional(),
    metadata: z.record(z.any()).optional(),
  })).optional(),
});

// TypeScript types inferred from schemas
export type PaymentInfo = z.infer<typeof PaymentInfoSchema>;
export type RefundInfo = z.infer<typeof RefundInfoSchema>;
export type Product = z.infer<typeof ProductSchema>;
export type PickupPoint = z.infer<typeof PickupPointSchema>;
export type OrderData = z.infer<typeof OrderDataSchema>;
export type FirebaseOrder = z.infer<typeof FirebaseOrderSchema>;

// Legacy types for backward compatibility (from original types.ts)
export interface OrderSummary {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  tiktokUsername: string;
  productReference: string;
  amount: number;
  deliveryMethod: "chronopost";
  paymentMethod: "stripe" | "paypal";
  pickupPoint: PickupPoint;
  status: "pending" | "paid" | "shipped" | "delivered" | "cancelled";
  createdAt: string;
}

export interface User {
  id: string;
  email: string;
  creditBalance: number;
  isSuperAdmin: boolean; // Indique si l'utilisateur est un super administrateur
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface CreditTransaction {
  id: string;
  userId: string;
  orderId: string;
  amount: number;
  type: "credit" | "debit";
  reason: string;
  createdAt: Timestamp;
  status: "pending" | "completed" | "failed";
  productIndex?: number;
}
